import "./AnimalsCard.css";
import type { AnimalRecord } from "../../api/animals";

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function safeUrl(value: unknown): string {
  try { const u = new URL(String(value)); return (u.protocol === "https:" || u.origin === window.location.origin) ? escapeHtml(u.href) : ""; }
  catch { return ""; }
}
function focalPercent(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 50;
  return Math.max(0, Math.min(100, value * 100));
}

function renderImages(animal: AnimalRecord): string {
  const images = Array.isArray(animal.images) ? animal.images.slice(0, 4) : [];
  if (!images.length) return "";

  return `
    <div class="animal-card-gallery animal-card-gallery-${images.length}">
      ${images
        .map((image, index) => {
          const src = image.thumbnail_url || image.image_url;
          const alt = image.alt_text || `${animal.common_name} image ${index + 1}`;
          return `
            <figure class="animal-card-image-slot animal-card-image-${index + 1}">
              <img
                src="${safeUrl(src)}"
                alt="${escapeHtml(alt)}"
                loading="${index === 0 ? "eager" : "lazy"}"
                referrerpolicy="no-referrer"
                style="object-position:${focalPercent(image.focal_x)}% ${focalPercent(image.focal_y)}%;"
              />
              <figcaption>
                <a href="${safeUrl(image.source_page_url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(image.attribution || image.creator || image.source_name || "Image source")}</a>
                <a href="${safeUrl(image.license_url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(image.license || "")}</a>
                <span>${escapeHtml(image.uploader ? "Uploaded by " + image.uploader : "")}</span>
                <span>${escapeHtml(image.modifications || "")}</span>
              </figcaption>
            </figure>
          `;
        })
        .join("")}
    </div>
  `;
}

function renderFacts(animal: AnimalRecord): string {
  const facts = [
    ["Habitat", animal.habitat],
    ["Diet", animal.diet],
    ["Lifespan", animal.lifespan],
    ["Status", animal.conservation_status],
    ["Weight", animal.weight],
    ["Length", animal.length],
    ["Range", animal.geographic_range],
  ].filter(([, value]) => String(value || "").trim());

  if (!facts.length) return "";

  return `
    <dl class="animal-card-facts">
      ${facts
        .map(
          ([label, value]) => `
            <div class="animal-card-fact">
              <dt>${escapeHtml(label)}</dt>
              <dd>${escapeHtml(value)}</dd>
            </div>
          `,
        )
        .join("")}
    </dl>
  `;
}

export function renderAnimalWebResults(animal: AnimalRecord): string {
  if (!Array.isArray(animal.sources) || !animal.sources.length) return "";
  const sources = [...animal.sources].sort((a, b) => Number(b.name === "Aikipedia") - Number(a.name === "Aikipedia"));
  return '<section class="animal-web-results" aria-label="Animal web results"><div class="serp-results-feed">' +
    sources.map((source) => {
      const href = safeUrl(source.url);
      if (!href) return "";
      const url = new URL(source.url);
      const displayUrl = url.hostname + " › " + decodeURIComponent(url.pathname).split("/").filter(Boolean).join(" › ");
      const title = (source.title || animal.common_name) + " — " + source.name;
      return '<article class="serp-result">' +
        '<div class="serp-result-copy">' +
          '<a class="serp-source-row" href="' + href + '" target="_blank" rel="noopener noreferrer">' +
            '<span class="serp-source-mark"><span class="serp-source-letter">' + escapeHtml(source.name.slice(0, 1)) + '</span></span>' +
            '<span class="serp-source-meta"><strong>' + escapeHtml(source.name) + '</strong><span>' + escapeHtml(displayUrl) + '</span></span>' +
          '</a>' +
          '<h2 class="serp-result-title"><a href="' + href + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(title) + '</a></h2>' +
          '<p class="serp-result-snippet">' + escapeHtml(animal.summary || "Read the reference article about " + animal.common_name + ".") + '</p>' +
        '</div>' +
      '</article>';
    }).join("") + '</div></section>';
}

export function renderAnimalsCard(animal: AnimalRecord): string {
  const subtitle = [animal.scientific_name, animal.animal_type]
    .filter((value) => String(value || "").trim())
    .join(" Â· ");

  return `
    <article class="animal-card" data-animal-slug="${escapeHtml(animal.slug)}">
      <header class="animal-card-header">
        <div>
          <div class="animal-card-kicker">AYESEARCH ANIMALS</div>
          <h1>${escapeHtml(animal.common_name)}</h1>
          ${subtitle ? `<p class="animal-card-subtitle">${escapeHtml(subtitle)}</p>` : ""}
        </div>
      </header>

      ${renderImages(animal)}

      ${
        animal.summary
          ? `<p class="animal-card-summary">${escapeHtml(animal.summary)}</p>`
          : ""
      }

      ${renderFacts(animal)}
    </article>
  `;
}
